import type { AlertSeverity, StatusKind } from '../types/status'
import type { SxProps, Theme } from '@mui/material/styles'

function getBrowserTimeZone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', {
    timeZone: getBrowserTimeZone()
  })
}

export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU')
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: getBrowserTimeZone()
  })
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, '0')
}

function isSameLocalDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
}

/** Local `dd.MM` for an ISO timestamp, e.g. `12.10`. */
export function formatDayMonth(value: string | null | undefined): string {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return '—'
  return `${padTwoDigits(date.getDate())}.${padTwoDigits(date.getMonth() + 1)}`
}

/** Local `HH:mm` for an ISO timestamp, e.g. `09:05`. */
export function formatTimeOfDay(value: string | null | undefined): string {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return '—'
  return `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`
}

/** Upper-case first letters of the first two words of a display name: «Антон Зайцев» → «АЗ». */
export function formatNameInitials(name: string | null | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toLocaleUpperCase('ru-RU'))
    .join('')
}

/** «сегодня в 14:47», «вчера в 14:47», «27.09 в 14:47» or «27.09.2025 в 14:47» in local time relative to `now`. */
export function formatRelativeDayTime(value: string | null | undefined, now: Date): string {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return '—'

  const time = formatTimeOfDay(value)
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  if (isSameLocalDay(date, now)) return `сегодня в ${time}`
  if (isSameLocalDay(date, yesterday)) return `вчера в ${time}`

  const dayMonth = formatDayMonth(value)
  const day = date.getFullYear() === now.getFullYear() ? dayMonth : `${dayMonth}.${date.getFullYear()}`
  return `${day} в ${time}`
}

/** Russian plural form for an integer count, e.g. `['позиция', 'позиции', 'позиций']`. */
export function selectRussianPluralForm(count: number, forms: readonly [one: string, few: string, many: string]): string {
  const mod10 = Math.abs(count) % 10
  const mod100 = Math.abs(count) % 100
  if (mod10 === 1 && mod100 !== 11) return forms[0]
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1]
  return forms[2]
}

/** «1 позиция», «2 позиции», «5 позиций». */
export function formatItemsCountLabel(count: number): string {
  return `${count} ${selectRussianPluralForm(count, ['позиция', 'позиции', 'позиций'])}`
}

/** Local calendar `yyyy-MM-dd` at 00:00:00 local → UTC ISO (for API `*AtUtc` fields). */
export function localCalendarDateToIsoUtc(dateStr: string): string {
  const parts = dateStr.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) {
    return new Date().toISOString()
  }
  const [y, m, d] = parts
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString()
}

export function getFileNameFromContentDisposition(contentDisposition: string | null | undefined): string | null {
  if (!contentDisposition) return null

  const parts = contentDisposition.split(';').map((part) => part.trim())
  const encodedPart = parts.find((part) => part.toLowerCase().startsWith('filename*='))
  if (encodedPart) {
    const rawValue = encodedPart.slice(encodedPart.indexOf('=') + 1).trim()
    const cleanedValue = rawValue.replace(/^UTF-8''/i, '')
    try {
      return decodeURIComponent(cleanedValue)
    } catch {
      return cleanedValue
    }
  }

  const plainPart = parts.find((part) => part.toLowerCase().startsWith('filename='))
  if (!plainPart) return null

  return plainPart.slice(plainPart.indexOf('=') + 1).trim().replace(/^"(.*)"$/, '$1')
}

export function sanitizeDownloadFileName(fileName: string | null | undefined, fallbackFileName: string): string {
  const candidate = (fileName || '').trim()
  const safeBase = candidate.length > 0 ? candidate : fallbackFileName
  const withoutUnsafeChars = safeBase
    .replace(/[/\\?%*:|"<>]/g, '_')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\.+$/g, '')
    .trim()

  if (!withoutUnsafeChars) {
    return fallbackFileName
  }

  return withoutUnsafeChars.length <= 120
    ? withoutUnsafeChars
    : withoutUnsafeChars.slice(0, 120).trim()
}

export function getStatusChipColor(status: string | null | undefined): 'success' | 'warning' | 'default' | 'primary' {
  if (status === 'В работе') return 'warning'
  if (status === 'Частично оплачена') return 'warning'
  if (status === 'Оплачена' || status === 'Закрыта') return 'success'
  if (status === 'Отменена') return 'default'
  if (status === 'Счета готовы' || status === 'Почти готовы счета' || status === 'Исполнена' || status === 'Частично исполнена') return 'default'
  return 'primary'
}

export function formatStatusChipLabel(status: string | null | undefined): string {
  if (!status) return '—'
  if (status === 'В оплату') return 'В оплате'
  if (status === 'Счета готовы' || status === 'Почти готовы счета') return status
  if (status === 'Исполнена') return 'Счета готовы'
  if (status === 'Частично исполнена') return 'Почти готовы счета'
  return status
}

export function isPaymentRequestStatus(status: string | null | undefined): boolean {
  return status === 'В оплату' || status === 'В оплате'
}

export function getStatusChipSx(status: string | null | undefined): SxProps<Theme> | undefined {
  if (status === 'В работе') {
    return {
      bgcolor: 'warning.dark'
    }
  }
  if (status === 'Частично оплачена') {
    return {
      bgcolor: 'common.white',
      borderColor: 'warning.main',
      color: 'warning.main'
    }
  }
  if (status === 'Оплачена') {
    return {
      bgcolor: 'common.white',
      borderColor: 'success.main',
      color: 'success.main'
    }
  }
  if (status === 'Счета готовы' || status === 'Почти готовы счета' || status === 'Исполнена' || status === 'Частично исполнена') {
    return {
      bgcolor: '#FFF8E1',
      borderColor: '#F59E0B',
      color: '#B45309'
    }
  }
  if (!isPaymentRequestStatus(status)) return undefined

  return {
    bgcolor: 'common.white',
    borderColor: 'primary.main',
    color: 'primary.main'
  }
}

export function alertSeverity(kind: StatusKind | null | undefined): AlertSeverity {
  if (kind === 'error') return 'error'
  if (kind === 'success') return 'success'
  return 'info'
}

export function formatFileSize(sizeInBytes: number | null | undefined): string {
  if (typeof sizeInBytes !== 'number' || !Number.isFinite(sizeInBytes) || sizeInBytes < 0) {
    return '—'
  }

  if (sizeInBytes < 1024) {
    return `${sizeInBytes} Б`
  }

  const units = ['КБ', 'МБ', 'ГБ']
  let value = sizeInBytes / 1024
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`
}

export function formatMeasurementUnitLabel(name: string | null | undefined, symbol: string | null | undefined): string {
  const normalizedName = (name || '').trim()
  const normalizedSymbol = (symbol || '').trim()

  if (normalizedName && normalizedSymbol) {
    return `${normalizedName} (${normalizedSymbol})`
  }

  return normalizedName || normalizedSymbol || 'Без единицы'
}

export function formatQuantityWithUnit(
  quantity: number | string | null | undefined,
  unit: string | null | undefined,
  fallbackUnit = 'ед.'
): string {
  const normalizedQuantity = quantity == null ? '—' : String(quantity)
  const normalizedUnit = (unit || '').trim()
  return `${normalizedQuantity} ${normalizedUnit || fallbackUnit}`
}

export const rubCurrencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 2
})

export function formatCurrency(
  amount: number | null | undefined,
  options?: { currency?: string; maximumFractionDigits?: number }
): string {
  if (amount == null || !Number.isFinite(amount)) return '—'

  const { currency = '₽', maximumFractionDigits = 2 } = options ?? {}

  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits
  }).format(amount)

  return `${formatted} ${currency}`
}

// ─── Payment / Fulfillment progress ──────────────────────────────────────────

export type PaymentProgress = 'NotStarted' | 'InProgress' | 'FullyPaid'
export type FulfillmentProgress = 'NotStarted' | 'InProgress' | 'FullyReceived'

const paymentProgressLabels: Record<PaymentProgress, string> = {
  NotStarted: 'Без оплаты',
  InProgress: 'Частично оплачен',
  FullyPaid: 'Оплачен'
}

const fulfillmentProgressLabels: Record<FulfillmentProgress, string> = {
  NotStarted: 'Без приёмки',
  InProgress: 'Частично принят',
  FullyReceived: 'Принят'
}

export function formatPaymentProgressLabel(progress: string | null | undefined): string {
  if (!progress) return '—'
  return paymentProgressLabels[progress as PaymentProgress] ?? progress
}

export function formatFulfillmentProgressLabel(progress: string | null | undefined): string {
  if (!progress) return '—'
  return fulfillmentProgressLabels[progress as FulfillmentProgress] ?? progress
}

export function getPaymentProgressColor(progress: string | null | undefined): 'success' | 'warning' | 'default' {
  if (progress === 'FullyPaid') return 'success'
  if (progress === 'InProgress') return 'warning'
  return 'default'
}

export function getFulfillmentProgressColor(progress: string | null | undefined): 'success' | 'warning' | 'default' {
  if (progress === 'FullyReceived') return 'success'
  if (progress === 'InProgress') return 'warning'
  return 'default'
}
